import {HttpClient, HttpErrorResponse, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {BehaviorSubject, Observable, of} from 'rxjs';

import {ConfigurationItem} from '../configuration-item';
import {ConfigurationService} from '../configuration.service';
import {User} from './user';

const sampleUser = {isAuthenticated: true, firstName: 'Randy', pictureUrl: 'My Picture URL', email: 'randy@aqaix.com'};

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private configuration;
  private authenticationHost: string;
  private userProfileUrl: string;
  private signoutUrl: string;

  // Note: refer to "Authentication Service" in this article:
  // http://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial

  private userBehaviorSubject: BehaviorSubject<User>;
  public userObservable;

  constructor(
    private configurationService: ConfigurationService,
    private http: HttpClient) {

    this.userBehaviorSubject = new BehaviorSubject<User>(undefined);
    this.userObservable = this.userBehaviorSubject.asObservable();
  }

  private initConfiguration() {
    this.configuration = this.configurationService.getConfiguration();
    const authenticationConfiguration: ConfigurationItem = this.configuration.authentication;
    this.authenticationHost = authenticationConfiguration.scheme + '://' + authenticationConfiguration.host;
    if (authenticationConfiguration.port) {
      this.authenticationHost += ':' + authenticationConfiguration.port;
    }
    this.createFullyQualifiedResourceNames();
  }

  private createFullyQualifiedResourceNames() {
    this.userProfileUrl = this.authenticationHost + '/user-profile';
    this.signoutUrl = this.authenticationHost + '/sign-out';
  }

  public isUserEntitledApiStore() {
    return Boolean(
      this.userBehaviorSubject.value &&
      this.userBehaviorSubject.value.isAuthenticated &&
      this.userBehaviorSubject.value.entitlements &&
      this.userBehaviorSubject.value.entitlements.includes('ApiStore')
    );
  }

  public clearUser() {
    this.userBehaviorSubject.next(undefined);
  }

  // called once when the app is initialized, which will happen on page reload, and after signIn.
  public init() {
    // return of(sampleUser);
    this.initConfiguration();
    return this.http.get(this.userProfileUrl, {withCredentials: true}).toPromise()
      .then((user: User) => {
        this.userBehaviorSubject.next(user);
        return user;
      })
      .catch((httpErrorResponse: HttpErrorResponse) => {
        this.clearUser();
        return true; // want to always continue successfully from this
      });
  }

  public signOut() {
    return this.http.post<void>(this.signoutUrl, {}, {withCredentials: true}).toPromise()
      .then(() => {
        this.clearUser();
        return true;
      });
  }

  public getSignInUrl(target) {
    const authenticationConfiguration: ConfigurationItem = this.configuration.authentication;
    const oauthState = {
      origin: authenticationConfiguration.origin,
      successPage: '/check-entitlements?target=' + target,
      errorPage: '/home'
    };
    const oauthStateString = btoa(JSON.stringify(oauthState));

    let redirectUri = `${authenticationConfiguration.scheme}://${authenticationConfiguration.host}`;
    if (authenticationConfiguration.port) {
      redirectUri += `:${authenticationConfiguration.port}`;
    }
    redirectUri += authenticationConfiguration.callbackResource;

    const httpParams = new HttpParams()
      .set('client_id', '169008152251-a613oj9qkmlqdtfutmguib69bljml1ti.apps.googleusercontent.com')
      .set('redirect_uri', redirectUri)
      .set('response_type', 'code')
      .set('scope', 'openid+profile+email')
      .set('prompt', 'consent')
      .set('state', oauthStateString);
    return 'https://accounts.google.com/o/oauth2/v2/auth?' + httpParams.toString();

    // return `https://accounts.google.com/o/oauth2/v2/auth
    // ?client_id=169008152251-a613oj9qkmlqdtfutmguib69bljml1ti.apps.googleusercontent.com
    //   &redirect_uri=${redirectUri}
    //   &response_type=code
    //   &scope=openid+profile+email
    //   &prompt=consent
    //   &state=${oauthStateString}`;
  }

  public handleHttpError401() {
    this.clearUser();
  }
}
